import { useMemo } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import { jobsEmploymentsOptions } from '@/constants/job-constants';
import { useWorkTypes } from './workTypesApi';

export interface WorkTypeOption {
  label: string;
  value: string | number;
}

/**
 * Source for the job/requisition "work type" dropdown.
 * Flag OFF → legacy static employment-type options (string enum values).
 * Flag ON  → enabled custom work types (numeric ids as values).
 *
 * Consumers branch their save payload on `isWorkTypesEnabled`:
 * ON → send `ats_work_type_id` (the chosen value), OFF → keep `employment_type`.
 */
export const useWorkTypeOptions = (): {
  options: WorkTypeOption[];
  isWorkTypesEnabled: boolean;
  isLoading: boolean;
} => {
  const { tCustomiseWorkTypes } = useKFlags();
  const isWorkTypesEnabled = Boolean(tCustomiseWorkTypes);
  const { data: workTypes = [], isLoading } = useWorkTypes({
    enabled: isWorkTypesEnabled,
  });

  const options = useMemo<WorkTypeOption[]>(() => {
    if (!isWorkTypesEnabled) return jobsEmploymentsOptions;
    return workTypes
      .filter((workType) => workType.enabled)
      .map((workType) => ({ label: workType.name, value: workType.id }));
  }, [isWorkTypesEnabled, workTypes]);

  return {
    options,
    isWorkTypesEnabled,
    isLoading: isWorkTypesEnabled && isLoading,
  };
};
